import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import Navbar from '../components/Navbar';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { 
  User, 
  Mail, 
  Shield, 
  CheckCircle2, 
  LogOut,
  Edit3,
  Camera,
  ChevronRight,
  MapPin,
  Phone,
  Calendar
} from 'lucide-react';
import EditProfileModal from '../components/EditProfileModal';
import DeleteAccountModal from '../components/DeleteAccountModal';

export default function UserProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const { user, logout, getDisplayName, fetchProfile } = useAuth();
  const { mobileOpen, toggleMobile } = useMobileMenu();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  useEffect(() => {
    // Ensure latest counts and profile data are loaded
    fetchProfile();
  }, [fetchProfile]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const displayName = getDisplayName();

  return (
    <>
      <Helmet>
        <title>My Profile | AdsWorld</title>
        <meta name="description" content="View and manage your account details and listings." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <span className="hover:text-primary-600 cursor-pointer" onClick={() => navigate('/')}>Home</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Profile</span>
          </nav>

          {/* Profile Header */}
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                  {user.image ? (
                    <img 
                      src={user.image} 
                      alt={displayName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    displayName.charAt(0).toUpperCase()
                  )}
                </div>
                <button className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-primary-600 transition-colors">
                  <Camera className="w-4 h-4" />
                </button>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
                  {user.is_verified && (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <p className="text-gray-500 mb-2">@{user.username}</p>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                    <Shield className="w-3 h-3" />
                    {user.is_verified ? 'Verified' : 'Unverified'}
                  </span>
                  {user.is_login_with_google && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">
                      Google
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button onClick={() => setEditOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-xl font-medium hover:bg-primary-100 transition-colors">
                  <Edit3 className="w-4 h-4" />
                  Edit Profile
                </button>
                <button 
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-xl font-medium hover:bg-red-100 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Account Details */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary-500" />
                  Account Information
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="font-medium text-gray-900">{displayName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p className="font-medium text-gray-900">{user.email}</p>
                    </div>
                    {user.is_verified && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="font-medium text-gray-900">{user.phone || 'Not added yet'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium text-gray-900">{(user as any).location || 'Not added yet'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Stats */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary-500" />
                  Your Activity
                </h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-primary-600">{user.total_ads ?? 0}</p>
                    <p className="text-sm text-gray-500">My Ads</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-primary-600">{user.saved_items_count ?? 0}</p>
                    <p className="text-sm text-gray-500">Saved</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-primary-600">0</p>
                    <p className="text-sm text-gray-500">Messages</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Links */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Access</h3>
                <div className="space-y-2">
                  <button onClick={() => navigate('/profile/my-ads')} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors text-left">
                    <span className="text-gray-700">My Ads</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button onClick={() => navigate('/profile/saved')} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors text-left">
                    <span className="text-gray-700">Saved Items</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button disabled className="w-full flex items-center justify-between p-3 rounded-xl transition-colors text-left text-gray-400 cursor-not-allowed">
                    <span>Messages (coming soon)</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button disabled className="w-full flex items-center justify-between p-3 rounded-xl transition-colors text-left text-gray-400 cursor-not-allowed">
                    <span>Settings (coming soon)</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Account Security */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-sm p-6 text-white">
                <Shield className="w-8 h-8 mb-3" />
                <h3 className="font-semibold mb-1">Security</h3>
                <p className="text-sm text-primary-100 mb-4">
                  Your account is secured with Google sign-in.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Extra protection available</span>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-red-100">
                <button 
                  onClick={() => setDeleteOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
      <EditProfileModal isOpen={editOpen} onClose={() => setEditOpen(false)} />
      <DeleteAccountModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </>
  );
}

import { Navigate } from 'react-router-dom';
